import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import DeleteUserDialog from "@/components/admin/user/DeleteUserDialog";
import { BACKEND, api, getToken } from "@/lib/api";
import { formatDate } from "@/lib/format";

const COPY = {
  resend: { title: "Ask them to retake their selfie?", desc: "Their verification goes back to unverified and they get the “verification_retake” notice.", label: "Resend verification" },
  reset: { title: "Reset today’s allowances?", desc: "Their daily Crushes and Winks refill now.", label: "Reset allowances" },
};

function ExportResult({ r }) {
  const href = `${BACKEND}${r.download_url}?auth=${encodeURIComponent(getToken() || "")}`;
  return (
    <div className="cw-tone-success max-w-md rounded-2xl px-4 py-3 text-xs" role="status" data-testid="user-export-result">
      <a href={href} className="inline-flex items-center gap-1.5 font-semibold underline" data-testid="user-export-download-link"><DownloadSimple size={14} />Download ZIP</a>
      <p className="mt-1">Link works until {formatDate(r.expires_at)}. {r.files?.length ? `${r.files.length} files.` : ""}</p>
    </div>
  );
}

// Role-scoped account tools. Owner: everything. Support: resend verification + export. Moderator: resend verification.
export default function UserTools({ user, role, firstName, onChanged }) {
  const owner = role === "owner";
  const [ask, setAsk] = useState(null);
  const [amount, setAmount] = useState("1");
  const [reason, setReason] = useState("");
  const [exp, setExp] = useState(null);
  const [del, setDel] = useState(false);

  const run = async () => {
    if (ask === "resend") { await api(`/admin/users/${user.id}/resend-verification`, { method: "POST" }); toast.success("Selfie retake requested."); }
    if (ask === "reset") { await api(`/admin/users/${user.id}/reset-allowances`, { method: "POST" }); toast.success("Allowances reset."); }
    if (ask === "bonus") {
      if (!(+amount >= 1 && +amount <= 100)) throw new Error("Choose 1 to 100.");
      const r = await api(`/admin/users/${user.id}/super-crush-bonus`, { body: { amount: +amount, reason: reason.trim() || null } });
      toast.success(`Added ${amount}. Super Crush balance: ${r.super_crush_balance}.`);
    }
    onChanged();
  };
  const doExport = async () => { try { setExp(await api(`/admin/users/${user.id}/export`, { method: "POST" })); } catch (e) { toast.error(e.message); } };
  const c = COPY[ask];

  return (
    <div className="flex flex-col items-end gap-3" data-testid="user-tools">
      {exp && <ExportResult r={exp} />}
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => setAsk("resend")} data-testid="user-resend-verification-button">Resend verification</Button>
        {(owner || role === "support") && <Button size="sm" variant="outline" onClick={doExport} data-testid="user-export-button">Export data</Button>}
        {owner && <>
          <Button size="sm" variant="outline" onClick={() => setAsk("reset")} data-testid="user-reset-allowances-button">Reset allowances</Button>
          <Button size="sm" variant="outline" onClick={() => { setAmount("1"); setReason(""); setAsk("bonus"); }} data-testid="user-bonus-button">Super Crush bonus</Button>
          <Button size="sm" variant="ghost" className="cw-error" onClick={() => setDel(true)} data-testid="user-delete-button">Delete account permanently</Button>
        </>}
      </div>
      <ConfirmDialog open={!!c} onOpenChange={(o) => !o && setAsk(null)} destructive={false} testId="user-tool-confirm-dialog" onConfirm={run}
        title={c?.title} description={c?.desc} confirmLabel={c?.label} />
      <ConfirmDialog open={ask === "bonus"} onOpenChange={(o) => !o && setAsk(null)} destructive={false} testId="user-bonus-dialog" onConfirm={run}
        title="Add bonus Super Crushes" confirmLabel="Add bonus" description="Recorded in their Super Crush ledger with reason “admin”.">
        <div className="grid grid-cols-[100px_1fr] gap-3">
          <div><Label htmlFor="bonus-amount">Amount</Label><Input id="bonus-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className="mt-2" data-testid="user-bonus-amount-input" /></div>
          <div><Label htmlFor="bonus-reason">Reason</Label><Input id="bonus-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" data-testid="user-bonus-reason-input" /></div>
        </div>
      </ConfirmDialog>
      {owner && <DeleteUserDialog open={del} onOpenChange={setDel} user={user} firstName={firstName} />}
    </div>
  );
}
